import React, { useState } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { updateGoal, deleteGoal, createGoalProgress } from "../../api/goals";
import PlanIntelligenceSuggestion from "./PlanIntelligenceSuggestion";
import PendingActionNotice from "../common/PendingActionNotice";

function parseOptionalNumber(value) {
  const cleaned = String(value ?? "").trim().replace(/,/g, "");
  if (!cleaned) {
    return { value: null };
  }
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return { error: "Starting point must be a number." };
  }
  return { value: parsed };
}

function GoalEditDialog({ goal, onClose, onSaved }) {
  const [title, setTitle] = useState(goal.title);
  const [unit, setUnit] = useState(goal.unit);
  const [targetValue, setTargetValue] = useState(goal.target_value);
  const [currentValue, setCurrentValue] = useState(goal.current_value ?? "");
  const [startingValue, setStartingValue] = useState(goal.starting_value ?? "");
  const dueDateValue = goal.target_date || goal.description?.match(/^By\s+(\d{4}-\d{2}-\d{2})$/)?.[1] || "";
  const initialDate = dueDateValue ? dayjs(dueDateValue) : null;
  const [targetDate, setTargetDate] = useState(initialDate);
  const [loading, setLoading] = useState(false);
  const [loadingMode, setLoadingMode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [tipsOpen, setTipsOpen] = useState(false);
  const [tipsDismissed, setTipsDismissed] = useState(false);

  function formatDateForStorage(date) {
    if (!date) return "";
    const nativeDate = date.toDate ? date.toDate() : date;
    const year = nativeDate.getFullYear();
    const month = String(nativeDate.getMonth() + 1).padStart(2, "0");
    const day = String(nativeDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  async function handleSave() {
    setLoading(true);
    setLoadingMode("save");
    setErrorMessage("");
    const targetDateString = formatDateForStorage(targetDate);
    const parsedStarting = parseOptionalNumber(startingValue);
    if (parsedStarting.error) {
      setErrorMessage(parsedStarting.error);
      setLoading(false);
      setLoadingMode("");
      return;
    }
    if (parsedStarting.value !== goal.starting_value) {
      const confirmed = window.confirm("Update where you started?");
      if (!confirmed) {
        setLoading(false);
        setLoadingMode("");
        return;
      }
    }
    try {
      await updateGoal(goal.id, {
        title,
        unit,
        target_value: targetValue,
        starting_value: parsedStarting.value,
        description: "",
        why_this_matters: goal.why_this_matters || "",
        target_date: targetDateString || null,
        timeframe: "once",
      });
      const previousAbsolute = Number(goal.current_value ?? 0);
      if (currentValue !== "" && Number(currentValue) !== Number(previousAbsolute || 0)) {
        const delta = Number(currentValue) - previousAbsolute;
        if (delta !== 0) {
          await createGoalProgress({
            goal: goal.id,
            value: delta,
          });
        }
      }
      setLoading(false);
      setLoadingMode("");
      onSaved?.({ updated: true, goalId: goal.id });
      onClose();
    } catch (err) {
      setErrorMessage(err?.message || "Unable to save this goal.");
      setLoading(false);
      setLoadingMode("");
    }
  }

  async function handleDelete() {
    if (!window.confirm("Delete this goal?")) return;

    setLoading(true);
    setLoadingMode("delete");
    setErrorMessage("");
    onSaved?.({ deleted: true, goalId: goal.id, optimistic: true });
    onClose();
    try {
      await deleteGoal(goal.id);
      onSaved?.({ deleted: true, goalId: goal.id });
    } finally {
      setLoading(false);
      setLoadingMode("");
    }
  }

  return (
    <Dialog open onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Goal</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {loading ? (
            <PendingActionNotice
              compact
              title={loadingMode === "delete" ? "Deleting goal" : "Saving goal"}
              body={loadingMode === "delete" ? "Removing this goal from your plan." : "Updating this goal and refreshing your plan."}
            />
          ) : null}

          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

          <TextField
            label="Goal title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            fullWidth
            autoFocus
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="By when?"
              value={targetDate}
              onChange={(value) => setTargetDate(value)}
              disablePast
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>

          <PlanIntelligenceSuggestion
            itemType="goal"
            payload={{
              title,
              description: goal.why_this_matters || "",
              target_value: targetValue,
              unit,
            }}
            applyLabel="Use suggested goal"
            onApply={(suggestion) => {
              if (suggestion.suggested_title) setTitle(suggestion.suggested_title);
            }}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Target value (end point)"
              type="number"
              value={targetValue}
              onChange={e => setTargetValue(Number(e.target.value))}
              fullWidth
              inputProps={{ step: "0.01" }}
            />
            <TextField
              label="Unit of measurement"
              value={unit}
              onChange={e => setUnit(e.target.value)}
              fullWidth
            />
          </Stack>

          {(() => {
            if (tipsDismissed) return null;
            const finite = /\b(finish|complete|read\b|read the\b|read entire|finish reading)\b/i.test(title || "");
            const shouldShow = Number(targetValue) === 1 || finite;
            if (!shouldShow) return null;
            const isReading = /\b(read|book|chapter|chapters|pages)\b/i.test(title || "");
            return (
              <Stack spacing={0.5} sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 2, p: 1.5 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setTipsOpen((prev) => !prev)}
                  sx={{ textTransform: "none", px: 0, minWidth: 0 }}
                >
                  {tipsOpen ? "Hide tips" : "Tips for tracking this goal"}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Tip: Goals feel easier to stick with when progress can be seen day to day.
                </Typography>
                {tipsOpen && (
                  <Typography variant="caption" color="text.secondary">
                    For big goals, most people track something measurable like pages, minutes, or steps instead of the
                    whole outcome. This helps progress feel real and motivating.
                  </Typography>
                )}
                {tipsOpen && isReading && (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 0.5 }}>
                    {["pages", "chapters", "minutes"].map((option) => (
                      <Button
                        key={option}
                        size="small"
                        variant="outlined"
                        onClick={() => setUnit(option)}
                        sx={{ textTransform: "none" }}
                      >
                        Track by {option}
                      </Button>
                    ))}
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setTipsDismissed(true)}
                      sx={{ textTransform: "none" }}
                    >
                      Keep as-is
                    </Button>
                  </Stack>
                )}
                {tipsOpen && !isReading && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setTipsDismissed(true)}
                    sx={{ textTransform: "none", px: 0, minWidth: 0 }}
                  >
                    Dismiss
                  </Button>
                )}
              </Stack>
            );
          })()}
          <TextField
            label="Where are you starting from? (optional)"
            value={startingValue}
            onChange={(e) => setStartingValue(e.target.value)}
            fullWidth
            inputProps={{ inputMode: "decimal", pattern: "[0-9., ]*" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="This baseline is used to measure progress from your start.">
                    <IconButton edge="end" size="small" aria-label="Starting point info">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Where are you today?"
            value={currentValue}
            onChange={(e) => setCurrentValue(e.target.value)}
            fullWidth
            inputProps={{ inputMode: "decimal", pattern: "[0-9., ]*" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Update this to log current progress from your starting point.">
                    <IconButton edge="end" size="small" aria-label="Current value info">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
        <Button color="error" onClick={handleDelete} disabled={loading}>
          Delete
        </Button>

        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!title.trim() || loading}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default GoalEditDialog;
